import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { STYLES, ASPECT_RATIOS, Style, AspectRatio, PromptStyle, PROMPT_STYLES } from './types';
import { generateImageAsync, generatePrompts } from '../../services/ideogramService';
import { toast } from '../ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Trash2 } from 'lucide-react';
import { Progress } from "../ui/progress";

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartGenerating?: () => void;
  refreshGallery?: () => void;
  initialPrompt?: string;
  isRemix?: boolean;
}

export const CreateDialog: React.FC<CreateDialogProps> = ({
  open,
  onOpenChange,
  onStartGenerating,
  refreshGallery,
  initialPrompt = '',
  isRemix = false
}) => {
  // Single prompt state
  const [style, setStyle] = useState<Style>('AUTO');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('ASPECT_16_9');
  const [prompt, setPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Batch prompts state
  const [batchText, setBatchText] = useState('');
  const [batchPrompts, setBatchPrompts] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  // Generate prompts state
  const [seedPrompt, setSeedPrompt] = useState('');
  const [promptStyle, setPromptStyle] = useState<PromptStyle>('Product Development');
  const [numberOfPrompts, setNumberOfPrompts] = useState(3);
  const [isGenerating, setIsGenerating] = useState(false);

  // Add state for active tab
  const [activeTab, setActiveTab] = useState('single');

  // Effect to handle initialPrompt
  useEffect(() => {
    if (open && initialPrompt) {
      setPrompt(initialPrompt);
    }
  }, [open, initialPrompt]);

  // Effect to reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setPrompt('');
      setStyle('AUTO');
      setAspectRatio('ASPECT_16_9');
    }
  }, [open]);

  const handleSingleSubmit = async () => {
    try {
      if (!prompt.trim()) {
        toast({
          title: "Error",
          description: "Please enter a prompt",
          variant: "destructive",
          duration: 3000
        });
        return;
      }

      setIsSubmitting(true);
      console.log('Starting image generation...');
      
      // Start image generation in background
      await generateImageAsync(prompt, aspectRatio, style);
      console.log('Image generation started successfully');
      
      // Reset form
      setStyle('AUTO');
      setAspectRatio('ASPECT_16_9');
      setPrompt('');
      
      // Show success message about image generation
      toast({
        title: "Image Generation Started",
        description: "Your image is being generated! The gallery will update automatically every 5 seconds.",
        duration: 5000
      });

      // Close dialog immediately
      onOpenChange(false);

      // Start polling
      console.log('Triggering polling start');
      onStartGenerating?.();
      
      // Set a timeout to refresh the gallery after 30 seconds
      setTimeout(() => {
        refreshGallery?.();
      }, 30000);
      
    } catch (error: any) {
      console.error('Error starting image generation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || error.message || "Failed to start image generation. Please try again.",
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchSubmit = () => {
    const prompts = batchText.split('\n').filter(p => p.trim() !== '');
    setBatchPrompts(prompts);
    setShowConfirmation(true);
  };

  const handleRemovePrompt = (index: number) => {
    setBatchPrompts(prev => prev.filter((_, i) => i !== index));
  };

  const handleBatchGenerate = async () => {
    try {
      setIsSubmitting(true);
      setShowProgress(true);
      
      // Quickly show progress for visual feedback
      for (let i = 0; i < batchPrompts.length; i++) {
        setCurrentPromptIndex(i);
        await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay for visual feedback
      }

      // Start all image generations in background
      batchPrompts.forEach(async (prompt) => {
        try {
          await generateImageAsync(prompt, aspectRatio, style);
        } catch (error) {
          console.error('Error generating image:', error);
        }
      });
      
      // Show success message about batch generation
      toast({
        title: "Batch Generation Started",
        description: `Your ${batchPrompts.length} images are being generated! Refresh the gallery in approximately 30 seconds.`,
        duration: 3000
      });
      
      // Reset form and close dialog immediately
      setBatchText('');
      setBatchPrompts([]);
      setShowConfirmation(false);
      setShowProgress(false);
      onOpenChange(false);

      // Start polling
      onStartGenerating?.();

      // Set a timeout to refresh the gallery after 30 seconds
      setTimeout(() => {
        refreshGallery?.();
      }, 30000);
    } catch (error) {
      console.error('Error in batch processing:', error);
      toast({
        title: "Error",
        description: "Failed to start batch generation. Please try again.",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSubmitting(false);
      setShowProgress(false);
    }
  };

  const handleGeneratedPrompts = async () => {
    if (!seedPrompt) return;

    setIsGenerating(true);
    try {
      const response = await generatePrompts({
        seedPrompt,
        promptStyle,
        numberOfPrompts,
      });
      setBatchText(response.prompts.join('\n'));
      setBatchPrompts(response.prompts);
      
      // Switch to batch tab after generating
      setActiveTab('batch');
      
      toast({
        title: "Prompts Generated",
        description: `Successfully generated ${response.prompts.length} prompts`,
        duration: 3000
      });
    } catch (error) {
      console.error('Error generating prompts:', error);
      toast({
        title: "Error",
        description: "Failed to generate prompts. Please try again.",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>
            {isRemix ? 'Remix Image' : 'Create Images'}
          </DialogTitle>
          <DialogDescription>
            {isRemix 
              ? 'Create a variation of this image by modifying the prompt or settings.' 
              : 'Create a single prompt or batch process multiple prompts.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="single">New Prompt</TabsTrigger>
            <TabsTrigger value="batch">Prompt Batch</TabsTrigger>
            <TabsTrigger value="generate">Generate Prompts</TabsTrigger>
          </TabsList>

          <TabsContent value="single">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="style">Style</label>
                <Select value={style} onValueChange={(value: Style) => setStyle(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STYLES.map((style) => (
                      <SelectItem key={style} value={style}>
                        {style}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="aspectRatio">Aspect Ratio</label>
                <Select value={aspectRatio} onValueChange={(value: AspectRatio) => setAspectRatio(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASPECT_RATIOS.map((ratio) => (
                      <SelectItem key={ratio} value={ratio}>
                        {ratio.replace('ASPECT_', '').replace('_', ':')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="prompt">Prompt</label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                />
              </div>

              <Button onClick={handleSingleSubmit} disabled={isSubmitting || !prompt.trim()}>
                {isSubmitting ? "Creating..." : "Create Image"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="batch">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="style">Style (applies to all prompts)</label>
                <Select value={style} onValueChange={(value: Style) => setStyle(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STYLES.map((style) => (
                      <SelectItem key={style} value={style}>
                        {style}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="aspectRatio">Aspect Ratio (applies to all prompts)</label>
                <Select value={aspectRatio} onValueChange={(value: AspectRatio) => setAspectRatio(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASPECT_RATIOS.map((ratio) => (
                      <SelectItem key={ratio} value={ratio}>
                        {ratio.replace('ASPECT_', '').replace('_', ':')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!showConfirmation ? (
                <>
                  <div className="grid gap-2">
                    <label htmlFor="batchPrompts">
                      Prompts (one per line)
                    </label>
                    <Textarea
                      id="batchPrompts"
                      value={batchText}
                      onChange={(e) => setBatchText(e.target.value)}
                      rows={8}
                      placeholder="Enter each prompt on a new line..."
                    />
                  </div>

                  <Button 
                    onClick={handleBatchSubmit} 
                    disabled={isSubmitting || !batchText.trim()}
                  >
                    Review Prompts
                  </Button>
                </>
              ) : (
                <>
                  <div className="border rounded-lg p-4 space-y-2 max-h-[300px] overflow-y-auto">
                    <h4 className="font-medium mb-2">Please confirm your prompts:</h4>
                    {batchPrompts.map((p, index) => (
                      <div key={index} className="flex items-center justify-between gap-2 p-2 bg-secondary rounded">
                        <span className="flex-1 break-all">{p}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemovePrompt(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col gap-4">
                    {showProgress && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Processing prompts...</span>
                          <span>{currentPromptIndex} of {batchPrompts.length}</span>
                        </div>
                        <Progress 
                          value={(currentPromptIndex / batchPrompts.length) * 100} 
                          className="h-2"
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowConfirmation(false)}
                        disabled={isSubmitting}
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleBatchGenerate}
                        disabled={isSubmitting || batchPrompts.length === 0}
                        className="flex-1"
                      >
                        {isSubmitting ? `Generating (${currentPromptIndex}/${batchPrompts.length})` : `Generate ${batchPrompts.length} Images`}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="generate">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <label htmlFor="seedPrompt">Seed Prompt</label>
                <Textarea
                  id="seedPrompt"
                  value={seedPrompt}
                  onChange={(e) => setSeedPrompt(e.target.value)}
                  rows={4}
                  placeholder="Enter your product description or concept"
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="promptStyle">Prompt Style</label>
                <Select
                  value={promptStyle}
                  onValueChange={(value: PromptStyle) => setPromptStyle(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMPT_STYLES.map((style) => (
                      <SelectItem key={style} value={style}>
                        {style}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="numberOfPrompts"># of Prompts</label>
                <Select
                  value={numberOfPrompts.toString()}
                  onValueChange={(value: string) => setNumberOfPrompts(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(10)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleGeneratedPrompts} 
                disabled={isGenerating || !seedPrompt.trim()}
              >
                {isGenerating ? "Generating..." : "Generate Prompts"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
